import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderLock, Download, Upload, History, FileText, Image, ShieldCheck, ShieldAlert, ShieldQuestion, Eye } from "lucide-react";

const CAT_LABEL = {
  case_documents: "Case Documents",
  documents_for_service: "Documents for Service",
  supporting_evidence: "Supporting Evidence",
  affidavit_return: "Affidavit / Return",
  photos_evidence: "Photos / Evidence",
  completed_documents: "Completed Documents",
};

function fmtSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// role: "client" | "admin" | "server". uploadCats controls which categories the user may add.
export function DocumentVault({ orderId, role = "client" }) {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(role === "server" ? "photos_evidence" : "supporting_evidence");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState({});

  const downloadUrl = (d) => `${API}${d.download_url.replace("/api", "")}`;
  const fileIdOf = (d) => {
    const m = (d.download_url || "").match(/\/files\/([^/?]+)/);
    return m ? m[1] : d.id;
  };

  // View: mint a short-lived, file-scoped token then open the file with a normal
  // top-level navigation so Chrome renders the PDF natively (Print + Download built in).
  // No blob, no iframe (Chrome blocks blob/iframe PDFs inside the sandboxed preview).
  // The secure /api/files endpoint + _can_access_file authorization run again on the GET.
  const openPreview = async (d) => {
    const fid = fileIdOf(d);
    if (!(d.download_url || "").includes("/files/")) { openFile(d); return; }
    const tab = window.open("about:blank", "_blank");
    try {
      const { data } = await api.post(`/files/${fid}/view-token`);
      const url = `${API}/files/${fid}?token=${encodeURIComponent(data.token)}&inline=1`;
      if (tab) tab.location.replace(url); else window.location.assign(url);
    } catch (err) {
      if (tab) tab.close();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to open document");
    }
  };

  // Download: fetch via the authenticated axios client and save the bytes.
  const openFile = async (d) => {
    const path = downloadUrl(d).replace(API, "");
    try {
      const resp = await api.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.data.type || d.content_type || "application/octet-stream" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = d.original_filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to open document");
    }
  };

  const verify = async (docId) => {
    setVerifying((v) => ({ ...v, [docId]: true }));
    try {
      const { data } = await api.post(`/documents/${docId}/verify`);
      if (data.result === "VERIFIED") toast.success("Integrity verified — hash matches");
      else toast.error("Integrity MISMATCH — document flagged for review");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setVerifying((v) => ({ ...v, [docId]: false })); }
  };

  function IntegrityBadge({ status }) {
    if (!status) return null;
    if (status === "verified") return (
      <span data-testid="integrity-verified" className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
        <ShieldCheck className="h-3 w-3" /> Integrity Verified
      </span>
    );
    return (
      <span data-testid="integrity-mismatch" className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
        <ShieldAlert className="h-3 w-3" /> Integrity Mismatch
      </span>
    );
  }

  const uploadCats = role === "server"
    ? ["photos_evidence", "supporting_evidence"]
    : ["case_documents", "documents_for_service", "supporting_evidence", "completed_documents"];

  const load = useCallback(() => {
    api.get(`/orders/${orderId}/documents`).then(({ data }) => setData(data)).catch(() => {});
  }, [orderId]);
  useEffect(load, [load]);

  const onUpload = async (e, groupCat) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/documents?category=${groupCat}`, (() => {
        const fd = new FormData(); fd.append("file", file); return fd;
      })(), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onNewVersion = async (e, docId) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/documents/${docId}/version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New version uploaded");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return null;
  const byCat = {};
  for (const d of data.documents) (byCat[d.category] = byCat[d.category] || []).push(d);
  const orderedCats = data.categories.filter((c) => byCat[c]?.length);

  return (
    <div data-testid="document-vault" className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
          <FolderLock className="h-5 w-5 text-slate-400" /> Document Vault
        </h3>
        <span className="flex items-center gap-1 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Secure</span>
      </div>

      {orderedCats.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No documents yet.</p>}

      <div className="space-y-5">
        {orderedCats.map((c) => (
          <div key={c} data-testid={`vault-category-${c}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{CAT_LABEL[c] || c}</p>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
              {byCat[c].map((d) => (
                <div key={d.id} data-testid={`vault-doc-${d.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    {d.content_type?.startsWith("image/") ? <Image className="h-4 w-4 shrink-0 text-slate-400" /> : <FileText className="h-4 w-4 shrink-0 text-slate-400" />}
                    <div className="min-w-0">
                      <button
                        type="button"
                        data-testid={`vault-doc-name-${d.id}`}
                        onClick={() => openPreview(d)}
                        title="Open / preview document"
                        className="block max-w-full truncate text-left text-sm font-medium text-slate-700 hover:text-emerald-700 hover:underline"
                      >{d.original_filename}</button>
                      <p className="text-xs text-slate-400">
                        v{d.version} · {fmtSize(d.size)} · {d.status}
                        {d.uploaded_by ? ` · ${d.uploaded_by}` : ""}
                      </p>
                      {!d.is_virtual && d.integrity_status && <div className="mt-1"><IntegrityBadge status={d.integrity_status} /></div>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {!d.is_virtual && d.sha256 && (
                      <Button data-testid={`vault-verify-${d.id}`} size="sm" variant="outline" disabled={verifying[d.id]} onClick={() => verify(d.id)} title="Verify integrity">
                        <ShieldQuestion className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button data-testid={`vault-preview-${d.id}`} size="sm" variant="outline" onClick={() => openPreview(d)}><Eye className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">View</span></Button>
                    <Button data-testid={`vault-download-${d.id}`} size="sm" variant="outline" onClick={() => openFile(d)}><Download className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Download</span></Button>
                    {!d.is_virtual && role !== "server" && d.category !== "affidavit_return" && (
                      <label data-testid={`vault-version-${d.id}`} className="cursor-pointer">
                        <input type="file" className="hidden" onChange={(e) => onNewVersion(e, d.id)} />
                        <span className="inline-flex h-8 items-center rounded-md border border-slate-200 px-2 text-slate-500 hover:bg-slate-50"><History className="h-3.5 w-3.5" /></span>
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="vault-upload-category" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            {uploadCats.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[c]}</SelectItem>)}
          </SelectContent>
        </Select>
        <label data-testid="vault-upload-button">
          <input type="file" className="hidden" disabled={busy} onChange={(e) => onUpload(e, cat)} />
          <span className={`inline-flex h-9 cursor-pointer items-center rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 ${busy ? "opacity-60" : ""}`}>
            <Upload className="mr-1.5 h-4 w-4" /> {busy ? "Uploading…" : "Upload document"}
          </span>
        </label>
      </div>
    </div>
  );
}
